import { useCallback, useEffect, useId, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { researchCms } from "@/api/researchCms.service";
import { regions } from "@/api/regions.service";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "@/components/Form/AsyncSearchableSelect/AsyncSearchableSelect";
import styles from "./CreateRankingEntityModal.module.css";

const ENTITY_TYPES = [
  { value: "country", label: "Country" },
  { value: "sub_region", label: "Sub-region" },
  { value: "bar", label: "Bar" },
  { value: "editorial", label: "Editorial" },
  { value: "trade_bloc", label: "Trade bloc" },
];

const PAGE_SIZE = 50;

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.string().min(1, "Type is required"),
  parentId: z.string().nullable(),
  taxonomyRegionId: z.string().nullable(),
});

type FormValues = z.infer<typeof schema>;

type CreateRankingEntityModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (entity: { id: string; name: string; type: string }) => void;
};

export function CreateRankingEntityModal({
  open,
  onClose,
  onCreated,
}: CreateRankingEntityModalProps) {
  const titleId = useId();
  const entityOffsetRef = useRef(0);
  const entityQueryRef = useRef("");
  const entityTotalRef = useRef(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", type: "country", parentId: null, taxonomyRegionId: null },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const searchEntities = useCallback(async (query: string): Promise<AsyncSelectOption[]> => {
    entityQueryRef.current = query;
    entityOffsetRef.current = 0;
    const result = await researchCms.getRankingEntities({
      search: query,
      limit: PAGE_SIZE,
      offset: 0,
    });
    const opts = result.researchRankingEntities.map(e => ({
      value: e.id,
      label: `${e.name} (${e.type})`,
    }));
    entityTotalRef.current = result.pagination?.total ?? opts.length;
    entityOffsetRef.current = PAGE_SIZE;
    return opts;
  }, []);

  const loadMoreEntities = useCallback(async () => {
    const result = await researchCms.getRankingEntities({
      search: entityQueryRef.current,
      limit: PAGE_SIZE,
      offset: entityOffsetRef.current,
    });
    const newOpts = result.researchRankingEntities.map(e => ({
      value: e.id,
      label: `${e.name} (${e.type})`,
    }));
    entityTotalRef.current = result.pagination?.total ?? 0;
    entityOffsetRef.current += PAGE_SIZE;
    return { options: newOpts, hasMore: entityOffsetRef.current < entityTotalRef.current };
  }, []);

  const searchRegions = useCallback(async (query: string): Promise<AsyncSelectOption[]> => {
    const all = await regions.getAll();
    const q = query.toLowerCase();
    return all
      .filter(r => !q || r.name.toLowerCase().includes(q))
      .slice(0, 30)
      .map(r => ({ value: r.id, label: r.name }));
  }, []);

  async function onSubmit(data: FormValues) {
    try {
      const result = await researchCms.createRankingEntity({
        name: data.name,
        type: data.type,
        parentId: data.parentId,
        taxonomyRegionId: data.taxonomyRegionId,
      });
      onCreated(result.researchRankingEntity);
      onClose();
    } catch (err) {
      setError("root.serverError", {
        type: "server",
        message: err instanceof Error ? err.message : "Failed to create ranking entity",
      });
    }
  }

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        className={styles.dialog}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId} className={styles.title}>
          Create ranking entity
        </h3>
        <p className={styles.description}>
          A ranking entity is a country, sub-region, bar, or editorial grouping that practice areas
          are organised under.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="entity-name">
                Name{" "}
                <span aria-hidden="true" style={{ color: "var(--color-danger, #ef4444)" }}>
                  *
                </span>
              </label>
              <input
                id="entity-name"
                className={styles.input}
                {...register("name")}
                placeholder="e.g. United Kingdom"
                autoFocus
              />
              {errors.name && <span className={styles.error}>{errors.name.message}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="entity-type">
                Type{" "}
                <span aria-hidden="true" style={{ color: "var(--color-danger, #ef4444)" }}>
                  *
                </span>
              </label>
              <select id="entity-type" className={styles.select} {...register("type")}>
                {ENTITY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.field}>
            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <AsyncSearchableSelect
                  name="parentId"
                  label="Parent entity"
                  value={field.value}
                  onChange={val => field.onChange(val ?? null)}
                  onSearch={searchEntities}
                  onLoadMore={loadMoreEntities}
                  placeholder="Search parent entity..."
                  minSearchLength={0}
                  helperText="Optional. Set a parent if this entity sits under another (e.g. a sub-region under a country)."
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <Controller
              name="taxonomyRegionId"
              control={control}
              render={({ field }) => (
                <AsyncSearchableSelect
                  name="taxonomyRegionId"
                  label="Taxonomy region"
                  value={field.value}
                  onChange={val => field.onChange(val ?? null)}
                  onSearch={searchRegions}
                  placeholder="Search taxonomy regions..."
                  minSearchLength={0}
                  helperText="Optional. Links this entity to a taxonomy region for classification."
                />
              )}
            />
          </div>

          {errors.root?.serverError && (
            <div className={styles.serverError} role="alert">
              {errors.root.serverError.message}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className={styles.createButton} disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create entity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
